import axios from "axios";
import { createElement } from "react";
import { toast } from "sonner";

export const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const api = axios.create({ baseURL: `${BACKEND}/api` });

let tokenGetter = null;
export const setTokenGetter = (fn) => { tokenGetter = fn; };

api.interceptors.request.use(async (config) => {
  if (tokenGetter) {
    const t = await tokenGetter();
    if (t) config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

const askReview = (ref) => api.post(`/content/blocks/${ref}/review`).then(() => { toast.dismiss(); toast.success("Thanks, a person will review it and let you know."); }).catch(() => toast.error("Couldn't request a review"));

api.interceptors.response.use((r) => r, (err) => {
  const d = err?.response?.data?.detail;
  if (d && typeof d === "object" && d.code === "mfa_required") {
    window.dispatchEvent(new CustomEvent("middle:approval-required", { detail: "mfa" }));
    err.response.data.detail = d.message;
  } else if (d && typeof d === "object" && d.code === "approval_required") {
    window.dispatchEvent(new CustomEvent("middle:approval-required", { detail: d.status || "pending" }));
    err.response.data.detail = d.message;
  } else if (typeof d === "string" && d.startsWith("Not posted:")) {
    const ref = (d.match(/\(ref ([0-9a-f]{8})\)/) || [])[1];
    err.response.data.detail = createElement("span", { "data-testid": "content-blocked-msg" }, d.replace(/ You can edit it.*$/, " "),
      ref && createElement("button", { onClick: () => askReview(ref), className: "underline font-medium", "data-testid": "content-review-request" }, "Ask for a human review"));
  }
  return Promise.reject(err);
});

export const imgUrl = (u) => (!u ? "" : u.startsWith("/api/") ? `${BACKEND}${u}` : u);
export const errMsg = (e) => e?.response?.data?.detail || "Something went wrong";

export const uploadImage = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/uploads", fd);
  return data.url;
};

export const timeAgo = (iso) => {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return new Date(iso).toLocaleDateString(LOCALE, { day: "2-digit", month: "2-digit", year: "numeric" });
};

// Global locale: dates follow the browser; prices use their own currency.
const safeLocale = () => { try { return Intl.getCanonicalLocales((navigator.language || "en").split("@")[0].replace("_", "-"))[0]; } catch { return "en"; } };
export const LOCALE = typeof navigator !== "undefined" ? safeLocale() : "en";
export const browserRegion = () => { try { return new Intl.Locale(LOCALE).maximize().region || ""; } catch { return ""; } };
export const browserCountry = () => { try { return new Intl.DisplayNames(["en"], { type: "region" }).of(browserRegion()) || ""; } catch { return ""; } };
export const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString(LOCALE, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "Not available";
export const fmtKm = (d) => (d === null || d === undefined ? "" : d < 1 ? "< 1 km away" : `${Math.round(d)} km away`);
export const track = (ev) => api.post("/interactions", ev).catch(() => {});
// Sends only the link: its preview (card, headline, summary) already says everything, so no extra message text.
// `via` tags how the link was sent (share menu, copied, or a named button) for the cookie-free share-channel stats.
export const withVia = (path, via) => `${window.location.origin}${path}${path.includes("?") ? "&" : "?"}via=${via}`;
export const shareLink = async (path, via) => {
  try { if (navigator.share) { await navigator.share({ url: withVia(path, via || "menu") }); return "shared"; } } catch { return "cancelled"; }
  const url = withVia(path, via || "copy");
  try { await navigator.clipboard.writeText(url); } catch { window.prompt("Copy this link", url); }
  return "copied";
};

